import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Crown, Check, ArrowUpRight, Flame, Camera, Users, TrendingDown,
  Shield, Sparkles, MessageCircle, Quote, ChevronDown, Share2,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PioneersCarousel from "@/components/PioneersCarousel";

const CLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

const COMMISSION_TIERS = [
  { range: "1 a 3", refs: "referidos activos", perk: "0% comisión en tus 3 primeras transacciones", icon: Users },
  { range: "4 a 9", refs: "referidos activos", perk: "0% comisión durante 6 meses + acceso anticipado a nuevas features", icon: TrendingDown },
  { range: "10 o más", refs: "referidos activos", perk: "0% comisión VITALICIA + foto destacada en home + voto en decisiones de producto", icon: Crown },
];

const FAQS = [
  {
    q: "¿Por qué $15.000 y no gratis?",
    a: "Porque queremos colegas comprometidos, no listas frías. Los $15.000 son una reserva simbólica que se abona íntegramente a tu suscripción anual del plan Pionero Fundador si cumples el reto (15 referidos validados). Además, con el reto activo ganas 0% de comisión en tus primeras 10 delegaciones.",
  },
  {
    q: "¿Qué pasa si no completo el reto?",
    a: "Al cabo de 3 meses vuelves automáticamente al plan Free. Si quieres seguir con beneficios, puedes suscribirte a Premium (0.85 UF/mes) o esperar la próxima ventana Pionero. Los $15.000 no se devuelven.",
  },
  {
    q: "¿Cómo cuento referidos activos?",
    a: "Un referido cuenta como validado cuando se registra con tu código de invitación, se verifica con su carnet o título y completa su perfil profesional.",
  },
  {
    q: "¿Es reembolsable?",
    a: "No. Por eso son solo 50 cupos. Esta etapa es para colegas que creen en el proyecto y quieren co-construir.",
  },
  {
    q: "¿Cuándo se abona la reserva a mi plan Fundador?",
    a: "Cuando completas el reto (15 referidos validados) y contratas la suscripción anual del plan Pionero Fundador. Los $15.000 se descuentan íntegros del precio anual (7 UF).",
  },
];

export default function EmbajadorPionero() {
  const [status, setStatus] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/membership/early-reserve/status").then((r) => setStatus(r.data)).catch(() => setStatus({
      founder_cap: 50, active_founders: 0, pending_reserves: 0, remaining: 50,
      reserve_amount_clp: 10000, founder_annual_price_clp: 120000,
    }));
  }, []);

  const handleCTA = () => {
    if (user?.is_embajador_pionero || user?.plan === "founder") {
      nav("/app/membresia");
      return;
    }
    if (!user) {
      nav("/registro/abogado?reserve=1&utm_source=pionero");
      return;
    }
    nav("/app/membresia/reservar");
  };

  const shareWhatsApp = () => {
    const refLine = user?.referral_code ? `\n\n🎟️ Usa mi código *${user.referral_code}* al registrarte.` : "";
    const text = `Mira lo que armaron para abogados/mediadores en Chile: *Colega Link*. Quedan menos de ${status?.remaining || 50} cupos Pionero por $15.000 CLP (se abonan al plan anual Pionero Fundador).${refLine}\n\nMírale acá 👉 https://www.colegalink.cl/embajador-pionero`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  if (!status) {
    return (
      <div className="container-app py-24 text-center">
        <p className="text-muted-foreground">Cargando…</p>
      </div>
    );
  }

  const taken = status.active_founders + status.pending_reserves;
  const pct = Math.min(100, Math.round((taken / status.founder_cap) * 100));
  const soldOut = status.remaining <= 0;

  return (
    <div className="relative" data-testid="pionero-landing">
      {/* Top bar of urgency */}
      <div className="border-b border-amber-400/30 bg-amber-400/5">
        <div className="container-app py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm">
          <Flame size={14} className="text-amber-400 animate-pulse" />
          <span className="text-amber-200">
            Quedan <span className="font-bold text-amber-300" data-testid="pionero-top-counter">{status.remaining}</span> cupos Pionero de {status.founder_cap}.
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 30% 20%, hsl(45 95% 55% / 0.35), transparent 55%)" }}
        />
        <div className="container-app pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-xs font-bold tracking-wider mb-6" data-testid="pionero-eyebrow">
              <Crown size={14} /> EMBAJADORES PIONEROS · SOLO 50 CUPOS
            </div>
            <h1 className="serif text-5xl md:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
              Sé uno de los <span className="text-amber-300">50 colegas</span> que escriben la primera página de Colega Link.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Por <span className="text-foreground font-semibold">{CLP(status.reserve_amount_clp)}</span> aseguras tu cupo Fundador
              de por vida, una insignia única en tu perfil y una <span className="text-amber-300">comisión 0% escalable</span> según los colegas que invites.
            </p>

            {/* CTA + counter */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={handleCTA}
                disabled={soldOut}
                className="btn-primary bg-amber-500 hover:bg-amber-400 text-amber-950 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pionero-hero-cta"
              >
                {soldOut ? "Cupos agotados" : <>Reservar mi cupo por {CLP(status.reserve_amount_clp)} <ArrowUpRight className="ml-2" size={16} /></>}
              </button>
              <button
                onClick={shareWhatsApp}
                className="inline-flex items-center gap-1.5 px-4 py-3 rounded-md text-sm font-medium bg-[#25D366] text-white hover:bg-[#1ebe5d] transition-colors"
                data-testid="pionero-share-whatsapp"
              >
                <Share2 size={14} /> Compartir por WhatsApp
              </button>
              <a href="#por-que" className="btn-ghost text-sm" data-testid="pionero-learn-more">
                ¿Por qué $15.000?
              </a>
            </div>

            {/* Progress bar */}
            <div className="mt-8 max-w-md" data-testid="pionero-progress">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                <span>{taken} de {status.founder_cap} cupos tomados</span>
                <span className="text-amber-300 font-medium">{pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all"
                  style={{ width: `${Math.max(2, pct)}%` }}
                />
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Inversión no reembolsable · se acredita íntegramente a tu primera anualidad Fundador ({CLP(status.founder_annual_price_clp)}).
              </div>
            </div>
          </div>

          {/* Right — sample badge card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-8 blur-3xl opacity-40" style={{ background: "radial-gradient(circle, hsl(45 95% 55% / 0.5), transparent 70%)" }} />
              <div className="relative panel p-8 ring-2 ring-amber-400/40 bg-gradient-to-br from-amber-950/30 to-background">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-amber-400/20 flex items-center justify-center ring-2 ring-amber-400/50">
                    <Crown size={26} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="text-xs text-amber-300 font-bold tracking-wider">PIONERO</div>
                    <div className="serif text-2xl">N°{Math.max(1, taken + 1).toString().padStart(2, "0")}</div>
                  </div>
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  <Bullet>Cupo Fundador <strong className="text-foreground">garantizado de por vida</strong></Bullet>
                  <Bullet>Insignia <strong className="text-amber-300">&ldquo;Pionero Fundador&rdquo;</strong> visible en tu perfil</Bullet>
                  <Bullet>Foto destacada en home cuando llegues a 10+ referidos</Bullet>
                  <Bullet><strong className="text-amber-300">Comisión 0%</strong> escalable según referidos activos</Bullet>
                  <Bullet>$15.000 acreditados íntegros a tu anualidad Fundador (7 UF)</Bullet>
                  <Bullet>Acceso al grupo cerrado de WhatsApp · co-construyes el producto</Bullet>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Por qué */}
      <section id="por-que" className="border-y" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="container-app py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow">Por qué $15.000</div>
            <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">
              No es un costo.<br />
              <span className="text-amber-300">Es tu lugar en la mesa.</span>
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Colega Link nace de y para abogados y mediadores chilenos. Los 50 Pioneros no son clientes:
              son <span className="text-foreground">socios estratégicos</span> que ayudan a validar, refinar
              y escalar la plataforma antes del lanzamiento masivo.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed text-sm">
              Los $15.000 cubren parte de los costos reales que asumimos por cada usuario activo (aviso prioritario de
              delegaciones, Colega IA, verificación de credenciales, infraestructura). Se abonan íntegros a tu suscripción anual
              del plan Pionero Fundador (7 UF) si completas el reto de 15 referidos validados.
            </p>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            <BenefitCard icon={Crown} title="Cupo Fundador asegurado" desc="Cuando lancemos los planes pagados, tu lugar entre los 50 Fundadores ya está reservado. El precio que pagas hoy se acredita íntegro a tu anualidad." />
            <BenefitCard icon={TrendingDown} title="0% comisión escalable" desc="A más colegas activos invites, menos comisión pagas. Hasta llegar a 0% vitalicia con 10+ referidos." />
            <BenefitCard icon={Camera} title="Foto destacada" desc="Tu fotografía aparece en la sección 'Nuestros Embajadores' del home público y en el dashboard de los nuevos usuarios." />
            <BenefitCard icon={Shield} title="Verificación prioritaria" desc="Tus credenciales (Corte Suprema, RUT, título) se validan en 24h hábiles antes del lanzamiento masivo." />
            <BenefitCard icon={MessageCircle} title="Grupo cerrado de Pioneros" desc="Acceso a un canal de WhatsApp privado donde decidimos juntos qué features priorizar." />
            <BenefitCard icon={Sparkles} title="Acceso anticipado" desc="Probarás las features nuevas (aviso prioritario de delegaciones, Colega IA, geolocalización, etc.) semanas antes que el resto." />
          </div>
        </div>
      </section>

      {/* Pioneers carousel — social proof */}
      <PioneersCarousel
        title="Nuestros Embajadores"
        subtitle="Las caras que están construyendo Colega Link contigo. Cada nuevo cupo se agrega acá automáticamente."
      />

      {/* Commission ladder */}
      <section className="container-app py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-eyebrow">Escala de comisión</div>
          <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">
            Cuantos más colegas traes, <span className="text-amber-300">menos comisión pagas.</span>
          </h2>
          <p className="mt-4 text-muted-foreground">
            Diseñado para que el Pionero más activo recupere los $15.000 al abonarlos a su plan anual Fundador
            y opere con 0% de comisión vitalicia.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {COMMISSION_TIERS.map((t, i) => (
            <div
              key={t.range}
              className={`panel p-7 relative ${i === 2 ? "ring-2 ring-amber-400/40 bg-amber-400/5" : ""}`}
              data-testid={`commission-tier-${i}`}
            >
              {i === 2 && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold tracking-wider">
                  PIONERO MÁXIMO
                </div>
              )}
              <t.icon size={20} className={i === 2 ? "text-amber-400" : "text-[hsl(var(--accent))]"} />
              <div className="mt-4 serif text-3xl">{t.range}</div>
              <div className="text-xs label-eyebrow mt-1">{t.refs}</div>
              <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{t.perk}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof / testimonial */}
      <section className="bg-[hsl(var(--card))]/40 border-y" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="container-app py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <Quote size={48} className="text-amber-400/40 absolute -top-4 -left-2" />
            <blockquote className="serif text-3xl md:text-4xl leading-tight relative">
              &ldquo;Esto es lo que nos faltaba: una red de colegas confiables, con pago seguro y sin perder horas
              llamando a contactos. Si lo hacen bien, va a cambiar el ejercicio en regiones.&rdquo;
            </blockquote>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center text-amber-300 font-bold">M.G.</div>
              <div>
                <div className="text-sm font-medium">M. González</div>
                <div className="text-xs text-muted-foreground">Abogada · Concepción · Embajadora Pionera N°01</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Stat n={status.founder_cap} label="Cupos totales" highlight />
            <Stat n={status.remaining} label="Cupos disponibles ahora" highlight />
            <Stat n="0%" label="Comisión vitalicia con 10+ referidos" />
            <Stat n="24h" label="Verificación prioritaria" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-app py-20">
        <div className="max-w-3xl mx-auto">
          <div className="label-eyebrow text-center">Preguntas frecuentes</div>
          <h2 className="serif text-4xl mt-3 text-center leading-tight">Antes de reservar tu cupo</h2>

          <div className="mt-10 space-y-3" data-testid="pionero-faqs">
            {FAQS.map((f, i) => (
              <button
                key={f.q}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left panel p-5 hover:bg-white/[0.03] transition-colors"
                data-testid={`pionero-faq-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium">{f.q}</span>
                  <ChevronDown size={18} className={`text-muted-foreground transition-transform shrink-0 ${openFaq === i ? "rotate-180" : ""}`} />
                </div>
                {openFaq === i && (
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-app pb-24">
        <div className="panel p-10 md:p-14 ring-2 ring-amber-400/40 text-center bg-gradient-to-br from-amber-950/30 via-background to-background relative overflow-hidden">
          <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 0%, hsl(45 95% 55% / 0.3), transparent 60%)" }} />
          <div className="relative">
            <Crown size={32} className="text-amber-400 mx-auto" />
            <h2 className="serif text-4xl md:text-5xl mt-4 leading-tight">
              Tu cupo {Math.max(1, taken + 1)} de {status.founder_cap} <span className="text-amber-300">te espera.</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              {soldOut
                ? "Los 50 cupos Pionero están agotados. Suscríbete a la lista de espera y te avisaremos si se libera alguno."
                : `Quedan ${status.remaining} cupos. Una vez completados, el programa cierra de forma permanente.`}
            </p>
            <button
              onClick={handleCTA}
              disabled={soldOut}
              className="btn-primary bg-amber-500 hover:bg-amber-400 text-amber-950 mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pionero-final-cta"
            >
              {soldOut ? "Cupos agotados" : <>Reservar mi cupo por {CLP(status.reserve_amount_clp)} <ArrowUpRight className="ml-2" size={16} /></>}
            </button>
            <div className="mt-6 text-xs text-muted-foreground">
              ¿Ya tienes cuenta? <Link to="/login" className="underline hover:text-foreground" data-testid="pionero-login-link">Inicia sesión</Link> para reservar más rápido.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <Check size={15} className="text-amber-400 mt-0.5 shrink-0" />
      <span className="text-muted-foreground">{children}</span>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, desc }) {
  return (
    <div className="panel p-6">
      <Icon size={18} className="text-amber-400" />
      <div className="serif text-xl mt-3">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}

function Stat({ n, label, highlight = false }) {
  return (
    <div className="panel p-5">
      <div className={`serif text-4xl ${highlight ? "text-amber-300" : ""}`}>{n}</div>
      <div className="text-xs label-eyebrow mt-2">{label}</div>
    </div>
  );
}
